import { sanitizeUrl } from "@braintree/sanitize-url";
import MuiButton from "@mui/material/Button";
import Link from "@mui/material/Link";
import {
  type ButtonActions,
  buttonActionToColor,
  buttonActionToVariant,
  classes,
} from "common/components/Elements/Button/Button";
import theme, { sx, type SxStyles, ThemeColors } from "core/utilities/theme";
import type React from "react";
import { type CSSProperties, type RefObject } from "react";
import { Link as RouterLink } from "react-router-dom";

interface ComponentProps {
  disabled?: boolean;
  small?: boolean;
  children: React.ReactNode;
  e2eId?: string;
  action: ButtonActions;
  mr?: boolean;
  ml?: boolean;
  fullWidth?: boolean;
  className?: SxStyles;
  title?: string;
  buttonRef?: RefObject<HTMLButtonElement>;
  inheritColor?: boolean; // use this when the button is not on white background color
  buttonIcon?: boolean;
  buttonIconSelected?: boolean;
  url: string;
  /** By default links are react router links. Flag it here if the link is external. */
  externalLink?: boolean;
  openInNewTab?: boolean;
  /** Handle links click event */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

/**
 * ButtonLink is similar to button except that it renders a link instead of a button
 * styles and props should stay similar otherwise
 */
function ButtonLink({
  children,
  disabled,
  e2eId,
  action,
  ml,
  mr,
  fullWidth,
  className,
  small,
  title,
  buttonRef,
  inheritColor,
  buttonIcon,
  buttonIconSelected,
  url,
  externalLink,
  openInNewTab,
  onClick,
}: ComponentProps) {
  // Warning: Style logic to be kept in sync with Button component

  const style: CSSProperties = { minWidth: "auto" };
  if (buttonIcon) {
    style.minWidth = 0;
    if (action !== "tertiary") style.padding = "5px 9px 4px 9px";
    if (buttonIconSelected) style.color = theme.palette.secondary.main;
  }

  // CSS trick to show the 'title' tooltip even though the button is disabled
  if (disabled) style.pointerEvents = "auto";

  if (action === "secondary" && !inheritColor) style.backgroundColor = "white";

  if (disabled !== true && action === "warning") {
    style.color = "white";
    style.backgroundColor = ThemeColors.warning;
  }

  if (disabled !== true && action === "deleteWarning") {
    style.color = ThemeColors.deleteWarning;
    style.borderColor = ThemeColors.deleteWarning;
  }

  if (fullWidth) {
    style.minWidth = undefined;
  }

  const component = externalLink ? Link : RouterLink;

  return (
    <MuiButton
      component={disabled ? MuiButton : component}
      to={disabled ? undefined : externalLink ? undefined : sanitizeUrl(url)} // when RouterLink
      href={disabled ? undefined : externalLink ? url : undefined} // when Link
      ref={buttonRef}
      size={small ? "small" : undefined}
      variant={buttonActionToVariant[action]}
      color={inheritColor ? "inherit" : buttonActionToColor[action]}
      sx={sx(classes.root, ml ? classes.ml : undefined, mr ? classes.mr : undefined, className)}
      disabled={disabled}
      fullWidth={fullWidth}
      data-e2e-button={e2eId || ""}
      data-e2e-button-link={true}
      title={title}
      style={style}
      target={openInNewTab ? "_blank" : "_self"}
      onClick={onClick}
    >
      {children}
    </MuiButton>
  );
}

export default ButtonLink;
